/**
 * AgentActionCard — composant de confirmation pour une action proposée par
 * l'agent (Phase 23, niveau 'confirm').
 *
 * Affiche le titre de l'action + ses params lisibles + 2 boutons "Exécuter"
 * et "Annuler". Décision B1 : inline dans la bulle de chat (pas modal).
 *
 * États :
 *  - pending  : 2 boutons actifs
 *  - executing : spinner
 *  - executed : badge ✓ + summary du résultat (+ lien download si export Excel)
 *  - cancelled : badge ✗ neutre
 *  - failed   : badge rouge avec error message
 */

import { useState } from 'react';
import { Check, X, Circle, CircleDot, Download, Play, type LucideIcon } from 'lucide-react';
import { api, downloadFile } from '../lib/api-client';

export type PendingAction = {
  id: string;
  actionName: string;
  /** Params validés (passés tels quels par le backend). */
  params: Record<string, unknown>;
  /** Résumé humain pré-construit côté backend. */
  summary: string;
};

type Status = 'pending' | 'executing' | 'executed' | 'cancelled' | 'failed';

type ConfirmResponse = {
  ok?: boolean;
  status: Status;
  result?: {
    summary: string;
    data?: Record<string, unknown>;
  };
  message?: string;
};

export function AgentActionCard({ action }: { action: PendingAction }) {
  const [status, setStatus] = useState<Status>('pending');
  const [resultSummary, setResultSummary] = useState<string | null>(null);
  const [resultData, setResultData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setStatus('executing');
    setError(null);
    try {
      const r = await api.post<ConfirmResponse>(
        `/api/agent/actions/${action.id}/confirm`,
        {},
        { domainSlug: undefined },
      );
      if (r.ok && r.result) {
        setStatus('executed');
        setResultSummary(r.result.summary);
        setResultData(r.result.data ?? null);
      } else {
        setStatus('failed');
        setError(r.message ?? 'Echec inconnu');
      }
    } catch (err) {
      setStatus('failed');
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    }
  }

  async function cancel() {
    try {
      await api.post(`/api/agent/actions/${action.id}/cancel`, {}, { domainSlug: undefined });
      setStatus('cancelled');
    } catch {
      // Si déjà cancellée côté serveur, on assume cancelled
      setStatus('cancelled');
    }
  }

  const downloadPath = (resultData?.downloadPath as string) ?? null;

  return (
    <div
      className={`mt-2 border-l-2 px-3 py-2.5 ${
        status === 'executed'
          ? 'border-emerald-500 bg-emerald-500/5'
          : status === 'failed'
            ? 'border-red-500 bg-red-500/5'
            : status === 'cancelled'
              ? 'border-ink-700 bg-ink-800'
              : 'border-amber-500 bg-amber-500/5'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 ${
            status === 'executed'
              ? 'text-emerald-400'
              : status === 'failed'
                ? 'text-red-400'
                : status === 'cancelled'
                  ? 'text-steel-500'
                  : 'text-amber-500'
          }`}
        >
          {(() => {
            const StatusIcon: LucideIcon =
              status === 'executed' ? Check : status === 'failed' ? X : status === 'cancelled' ? Circle : CircleDot;
            return <StatusIcon aria-hidden className="w-5 h-5" strokeWidth={1.75} />;
          })()}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-2xs font-mono uppercase tracking-widest text-steel-500 mb-0.5">
            {status === 'executed'
              ? 'action exécutée'
              : status === 'failed'
                ? 'échec'
                : status === 'cancelled'
                  ? 'annulée'
                  : 'action proposée'}
            {' · '}
            <span className="text-amber-500 normal-case tracking-normal">{action.actionName}</span>
          </div>
          <div className="text-sm text-steel-100">
            {status === 'executed' && resultSummary ? resultSummary : action.summary}
          </div>
          {error && (
            <div className="text-2xs text-red-400 mt-1 font-mono">{error}</div>
          )}

          {/* Affiche les alternatives (request_alternative_skus) */}
          {status === 'executed' && Array.isArray(resultData?.alternatives) && (
            <ul className="mt-2 space-y-1 text-2xs font-mono">
              {(resultData!.alternatives as Array<Record<string, unknown>>).map((alt, i) => (
                <li key={i} className="text-steel-300">
                  <span className="text-amber-500">{String(alt.sku)}</span>
                  {' · '}
                  <span>{String(alt.designation ?? '')}</span>
                  {alt.prix_unitaire_ht != null && (
                    <span className="text-steel-500"> · {Number(alt.prix_unitaire_ht).toFixed(2)} €</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Bouton télécharger pour export_excel */}
          {status === 'executed' && downloadPath && (
            <button
              onClick={() =>
                downloadFile(downloadPath).catch((err) =>
                  setError(err instanceof Error ? err.message : 'Erreur téléchargement'),
                )
              }
              className="mt-2 text-2xs font-mono uppercase tracking-widest text-amber-500 hover:text-amber-400 inline-flex items-center gap-1.5"
            >
              <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>télécharger l'Excel</span>
            </button>
          )}

          {/* Boutons d'action */}
          {status === 'pending' && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={confirm}
                className="tc-btn-primary text-2xs inline-flex items-center gap-1.5"
              >
                <Play aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span>exécuter</span>
              </button>
              <button
                onClick={cancel}
                className="text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-red-400"
              >
                annuler
              </button>
            </div>
          )}
          {status === 'executing' && (
            <div className="text-2xs font-mono text-steel-500 mt-1">⏳ exécution…</div>
          )}
        </div>
      </div>
    </div>
  );
}
